import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { isInteger } from 'lodash';
import { useEffect, useId, useMemo, useState } from 'react';
import { RRule } from 'rrule';
import * as Yup from 'yup';
import { TaskModal } from '@/modules/admin-dashboard/components/modals/TaskModal.tsx';
import { createOptionsFromUser } from '@/modules/admin-dashboard/components/modals/UpdateTaskModal.tsx';
import { sanitizeTaskReminders } from '@/modules/admin-dashboard/helpers/sanitizeTaskReminders.ts';
import { useCreateTaskMutation } from '@/modules/admin-dashboard/queries/tasks.ts';
import { useUsersListQuery } from '@/modules/admin-dashboard/queries/users.ts';
import { BaseModalProps, SelectPaginateOption } from 'ui';
import { CreateTaskRequest, ReminderMode, TaskPriority, UserRole } from 'compliance-api';

const initialValues: TaskFormValues = {
  name: '',
  priority: TaskPriority.Normal,
  start_date: '',
  first_due_date: '',
  recurrency_rule: '',
  user_ids: [],
  users_overdue_subs: [],
  users_completed_subs: [],
  department_ids: [],
  is_every_employee_needs_to_complete: false,
  incident_result_id: undefined,
  form_id: 0,
  description: '',
  due_remainders: [],
  client_id: undefined,
  preservedFullOptions: {
    department_ids: [],
    user_ids: [],
    users_completed_subs: [],
    users_overdue_subs: [],
    client_id: undefined,
  },
};

export interface TaskFormValues extends CreateTaskRequest {
  preservedFullOptions: {
    department_ids: SelectPaginateOption<number>[];
    user_ids: SelectPaginateOption<number>[];
    users_completed_subs: SelectPaginateOption<number>[];
    users_overdue_subs: SelectPaginateOption<number>[];
    client_id?: SelectPaginateOption<number>;
  };
}

export const taskValidationSchema = Yup.object().shape({
  name: Yup.string().required('Task name is required'),
  first_due_date: Yup.date().required('Due date is required'),
  start_date: Yup.date()
    .required('Start date is required')
    .when('first_due_date', ([first_due_date], schema) => {
      return first_due_date
        ? schema.max(first_due_date, 'Start date cannot be after the first due date')
        : schema;
    }),
  form_id: Yup.number().min(1, 'Form is required'),
  user_ids: Yup.array().of(Yup.number()),
  department_ids: Yup.array().of(Yup.number()),
  due_remainders: Yup.array().of(
    Yup.object().shape({
      days_before_due: Yup.number()
        .test('integer', 'Please enter an integer value greater than 0', function (reminder) {
          return this.parent.is_when_overdue ? true : isInteger(reminder);
        })
        .max(90, 'Days before due date must be at most 90 days')
        .required(),
      frequency: Yup.string()
        .nullable()
        .when('mode', {
          is: ReminderMode.FixedInterval,
          then: schema => schema.notRequired(),
          otherwise: schema =>
            schema
              .required('This field is required')
              .test('frequency', 'This field is required', function (frequency) {
                if (!frequency) return false;
                const currentRRule = RRule.fromString(frequency);
                const {
                  origOptions: { freq, interval },
                } = currentRRule;

                return this.parent.is_when_overdue || (isInteger(freq) && isInteger(interval));
              }),
        }),
      interval_days: Yup.number().when('mode', {
        is: ReminderMode.FixedInterval,
        then: schema =>
          schema
            .required('Every N days is required')
            .integer('Every N days must be a whole number')
            .min(1, 'Every N days must be at least 1 day')
            .max(90, 'Every N days must be at most 90 days'),
        otherwise: schema => schema.notRequired(),
      }),
      iterations: Yup.number().when('mode', {
        is: ReminderMode.FixedInterval,
        then: schema =>
          schema
            .required('Repeat times is required')
            .integer('Repeat times must be a whole number')
            .min(1, 'Repeat times must be at least 1')
            .max(90, 'Repeat times must be at most 90'),
        otherwise: schema => schema.notRequired(),
      }),
      stop_after: Yup.number()
        .min(1, 'Stop after must be at least 1 day')
        .max(90, 'Stop after must be at most 90 days')
        .integer('Stop after must be a whole number')
        .nullable(),
    }),
  ),
});

const excludedNotificationEmails = ['quantumrealm.ai', 'nosis.io'];

interface CreateTaskModalProps extends BaseModalProps {
  defaultForm?: SelectPaginateOption<number> | null;
  defaultIncident?: SelectPaginateOption<{ id: number }> | null;
  clientId?: number;
}

export const CreateTaskModal = ({
  open,
  onClose,
  defaultForm,
  defaultIncident,
  clientId,
}: CreateTaskModalProps) => {
  const [wereAdminsPrefilled, setWereAdminsPrefilled] = useState(false);
  const {
    data: adminsData,
    fetchNextPage: fetchNextAdminsPage,
    hasNextPage: hasNextAdminsPage,
    isLoading: isLoadingAdmins,
    isFetchingNextPage: isFetchingNextAdminsPage,
  } = useUsersListQuery(
    { page: 1, page_size: 15, user_role: UserRole.Admin },
    { cacheKey: useId() },
  );

  const admins = useMemo(
    () =>
      (adminsData?.pages || [])
        .flatMap(({ data }) => data)
        .filter(({ email }) => excludedNotificationEmails.every(e => !email.endsWith(e))),
    [adminsData?.pages],
  );

  useEffect(() => {
    if (hasNextAdminsPage && !isLoadingAdmins && !isFetchingNextAdminsPage) {
      fetchNextAdminsPage();
    }
  }, [fetchNextAdminsPage, hasNextAdminsPage, isFetchingNextAdminsPage, isLoadingAdmins]);

  useEffect(() => {
    if (admins.length && !isLoadingAdmins && !isFetchingNextAdminsPage && !wereAdminsPrefilled) {
      setWereAdminsPrefilled(true);
      formik.setFieldValue(
        'preservedFullOptions.users_overdue_subs',
        createOptionsFromUser(admins),
      );
      formik.setFieldValue(
        'users_overdue_subs',
        admins.map(({ id }) => id),
      );
      formik.setFieldValue(
        'preservedFullOptions.users_completed_subs',
        createOptionsFromUser(admins),
      );

      formik.setFieldValue(
        'users_completed_subs',
        admins.map(({ id }) => id),
      );
    }
  }, [admins, isFetchingNextAdminsPage, isLoadingAdmins, wereAdminsPrefilled]);

  const handleCloseModal = () => {
    formik.resetForm();
    onClose();
  };

  const isPastDate = (date: string) => date && dayjs(date).isBefore(dayjs(), 'day');

  const formik = useFormik<TaskFormValues>({
    initialValues: {
      ...initialValues,
      form_id: defaultForm?.value || 0,
      incident_result_id: defaultIncident?.value?.id || undefined,
      client_id: clientId,
    },
    validationSchema: taskValidationSchema,
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
    onSubmit: async (values, { setFieldError }) => {
      if (isPastDate(values.start_date)) {
        setFieldError('start_date', 'Start date cannot be in the past');
        return;
      }

      if (isPastDate(values.first_due_date)) {
        setFieldError('first_due_date', 'Due date cannot be in the past');
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { preservedFullOptions, ...taskData } = values;

      await createTask(sanitizeTaskReminders(taskData));
      handleCloseModal();
    },
  });

  const { mutateAsync: createTask, isLoading } = useCreateTaskMutation(formik.setErrors);

  return (
    <TaskModal
      isTaskDataLoading={isLoadingAdmins || isFetchingNextAdminsPage}
      formik={formik}
      defaultForm={defaultForm}
      defaultIncident={defaultIncident}
      handleCloseModal={handleCloseModal}
      isOpened={open}
      isTaskLoading={isLoading}
      clientId={clientId}
    />
  );
};
